import { watch, computed, ref } from 'vue';
import { useBroadcastChannel } from '@vueuse/core';
import { setTheme } from '@/scripts/mixins/themeManager';
import { useUserStore } from '@/vue/stores/user';
import { Theme } from '@/scripts/constants/themes';

const THEME_BROADCAST_CHANNEL = 'theme-sync';

const getDefaultTheme = (): Theme => {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDark ? Theme.DARK : Theme.DEFAULT;
};

const themeSuspended = ref(false);

export const suspendTheme = () => {
    themeSuspended.value = true;
};

export const resumeTheme = () => {
    themeSuspended.value = false;
};

export const useUserTheme = () => {
    const userStore = useUserStore();
    const { post, data, isSupported } = useBroadcastChannel<{ theme: Theme }, { theme: Theme }>({
        name: THEME_BROADCAST_CHANNEL,
    });

    const computedTheme = computed(() => {
        if (themeSuspended.value) {
            return getDefaultTheme();
        }

        return userStore.theme;
    });

    const applyTheme = (theme: Theme) => {
        if (!themeSuspended.value) {
            setTheme(theme);
        }

        if (isSupported.value) {
            try {
                post({ theme });
            } catch (error) {
                console.warn('Failed to broadcast theme change:', error);
            }
        }
    };

    watch(computedTheme, (newTheme) => {
        applyTheme(newTheme);
    }, { immediate: true });

    watch(data, (message) => {
        if (message?.theme) {
            const newTheme = message.theme;
            if (userStore.theme !== newTheme &&
                (newTheme === Theme.DEFAULT || newTheme === Theme.DARK)) {
                userStore.theme = newTheme;
            }
        }
    });
};
